import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const consultingId = searchParams.get('consulting_id');
  const itemId = orderId || consultingId;
  const isConsulting = !!consultingId;

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setVerifying(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setVerifyError(err.detail || 'Erreur de vérification du paiement');
        }
      } catch (e) {
        setVerifyError('Impossible de vérifier le paiement');
      } finally {
        setVerifying(false);
      }
    };
    verifyPayment();
  }, [sessionId]);

  useEffect(() => {
    if (verifying) return;
    // Auto-redirect after 5 seconds once verification is done
    const timer = setTimeout(() => {
      if (isConsulting) {
        navigate(`/client/consultings/${itemId}`);
      } else {
        navigate(`/client/orders/${itemId}`);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [verifying, itemId, isConsulting, navigate]);

  const goToItem = () => {
    if (isConsulting) {
      navigate(`/client/consultings/${itemId}`);
    } else {
      navigate(`/client/orders/${itemId}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <Card className="max-w-md w-full bg-[#1a1a1a] border-green-500/30">
        <CardContent className="p-8 text-center">
          {verifying ? (
            <>
              <Loader2 className="w-20 h-20 text-green-500 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-white mb-4">
                Vérification du paiement...
              </h1>
              <p className="text-[#A6A6A6]">
                Veuillez patienter quelques instants.
              </p>
            </>
          ) : verifyError ? (
            <>
              <AlertCircle className="w-20 h-20 text-orange-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4">
                Paiement reçu
              </h1>
              <p className="text-[#A6A6A6] mb-6">
                {verifyError}. Notre équipe va le vérifier manuellement sous peu.
              </p>
              <Button onClick={goToItem} className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white" data-testid="see-item-btn">
                {isConsulting ? 'Voir mon consulting' : 'Voir ma commande'}
              </Button>
            </>
          ) : (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4">
                Paiement Réussi !
              </h1>
              <p className="text-[#A6A6A6] mb-6">
                Votre {isConsulting ? 'consulting' : 'commande'}{' '}
                <span className="text-white font-semibold">{itemId}</span> a été payé avec succès.
              </p>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                <p className="text-green-400 text-sm">
                  ✓ Paiement confirmé<br />
                  ✓ Nos ingénieurs ont été notifiés<br />
                  ✓ Votre dossier est en cours de traitement
                </p>
              </div>
              <Button onClick={goToItem} className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white" data-testid="see-item-btn">
                {isConsulting ? 'Voir mon consulting' : 'Voir ma commande'}
              </Button>
              <p className="text-[#A6A6A6] text-sm mt-4">
                Vous serez redirigé automatiquement dans quelques secondes...
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
